/**
 * Promotions: all UI strings for the promotions list,
 * promotion details, and related modals.
 */

export const promotionsEn = {
  promotions: {
    // Page / section titles
    pageTitle: 'Promotions',
    addPromotion: 'Add Promotion',
    missingCategory: 'Missing Category!',
    limitedAccessWarning: 'Editing options for this promotion are limited due to your access level',

    // Banner messages
    banners: {
      bulkEditTitle: 'Bulk Edit your Promotions',
      bulkEditMessage:
        'If you would like to make bulk updates to your promotions, you can navigate to Quick Update to make changes with fewer clicks.',
      bulkEditAction: 'Go to Quick Update',
      categoryBoostMessage:
        'Categorizing your promotion will boost its visibility on rental listing sites.',
    },

    // Data table column headers
    fields: {
      title: 'Title',
      category: 'Category',
      startDate: 'Start Date',
      endDate: 'End Date',
      numberOfProperties: '# of Properties',
      lastModified: 'Last Modified',
      status: 'Status',
    },

    // Status select options (value → display text)
    statuses: {
      placeholder: 'Select Status',
      enabled: 'In Progress',
      disabled: 'Disabled',
      expired: 'Expired',
      scheduled: 'Scheduled',
    },

    // Single promotion page
    single: {
      // Section headings
      sectionDetails: 'Details',
      sectionCategory: 'Category',
      sectionLocations: 'Locations [{count}]',

      // Access / expiry banners
      accessWarning: 'Editing options for this promotion may be limited due to your access level. Contact your admin to provide access.',
      expiredWarning: 'This promotion is expired and will no longer show on your listings or website',

      // Form field labels
      labelTitle: 'Promotion Title',
      labelImage: 'Upload Image',
      labelImagePrompt: 'Choose Image',
      labelPdf: 'Upload PDF',
      labelPdfPrompt: 'Choose PDF',
      labelStatus: 'Status',
      labelStartDate: 'Start Date',
      labelEndDate: 'End Date',
      labelContent: 'Promotion Content',
      labelCategory: 'Promotion Category',

      // Validation / helper messages
      uploadFailed: 'Upload failed. Please try again or choose a different file.',
      fieldRequired: '{field} is required',
      mustBePositiveNumber: 'Must be a positive number',
      startDateRequired: 'Start Date is required!',
      legalDisclaimer: 'Be sure to include all applicable legal terms and conditions in your promotion content to stay fully compliant and protected.',
      exampleLabel: 'Example: {example}',

      // Buttons
      btnEdit: 'Edit',
      btnDuplicate: 'Duplicate Promotion',
      btnSaveDetails: 'Save Details',

      // Tooltips / swal dialogs
      deleteTooltipNoPermission: 'This promotion cannot be deleted with your current permissions.',
      duplicateTooltipSaveFirst: 'Save this promotion before creating a duplicate.',
      duplicateTooltipNoAccess: 'You cannot duplicate promotions with your current access level.',
      duplicateTooltipDefault: 'Duplicates current promotion. Note that unsaved changes to the current promotion will not be duplicated.',

      // Field help tooltips
      tooltipImage: 'Any images you add to this promotion will appear on applicable rental listing sites and/or websites.',
      tooltipPdf: 'PDF uploads are only displayed on Rentsync websites. If you do not have one, uploading a PDF will have no effect.',
      tooltipCategory: 'Promotion categories will only display on applicable rental listing sites that support the promotion category filter and tag; please contact support for more details.',

      // Save button tooltips
      saveTooltipNoPermission: 'You do not have permission to update this promotion.',
      saveTooltipNeedTitleAndCategory: 'Please enter a promotion title and select a promotion category.',
      saveTooltipNeedTitle: 'Please enter a promotion title.',
      saveTooltipNeedCategory: 'Please select a promotion category.',
      saveTooltipCategoryErrors: 'Please clear all category validation errors before saving.',
      saveTooltipInvalidFile: 'Invalid file type. Please upload a supported file format.',
      saveTooltipNeedStartDate: 'Please select a start date.',
      saveTooltipEndBeforeStart: 'End date must be after the start date.',

      // Swal dialogs & toasts
      removeTitle: 'Remove Promotion',
      removeText: 'Are you sure you want to remove this promotion?',
      removeConfirm: 'Yes, remove promotion',
      duplicateCompleteTitle: 'Promotion Duplication Complete',
      duplicateCompleteText: 'Would you like to navigate to your new promotion?',
      duplicateCompleteConfirm: 'Yes, navigate to new promotion',
      duplicateCompleteDeny: 'No, stay here',
      toastSaved: 'Promotion Details Saved!',
      toastSaveError: 'An error occurred saving this promotion! Please try again.',

      // Duplicate modal
      duplicateModalTitle: 'Duplicate Promotion',
      duplicateModalBtnNext: 'Next',
      duplicateModalBtnSubmit: 'Duplicate Promotion',
      duplicateModalIntro: 'You\'re about to duplicate "{title}". Choose how you\'d like to handle properties. You can add additional properties to the new promotion on the next screen.',
      duplicateModalMoveLabel: 'Copy content and move properties —',
      duplicateModalMoveDesc: 'Transfers properties you have access to from the original promotion to the new one, removing them from the original.',
      duplicateModalCopyLabel: 'Copy content and copy properties —',
      duplicateModalCopyDesc: 'Copies properties you have access to into the new promotion and leaves them on the original promotion.',
      duplicateModalCopyWarning: '(Warning: this may create duplicate promotions.)',
      duplicateModalNoneLabel: 'Copy content and select new properties',
      duplicateModalSuffix: '[Duplicated]',
      duplicateModalToastSuccess: 'Promotion duplicated!',
      duplicateModalToastError: 'There was an error duplicating this promotion!',
    },

    // RsModalPromotionProperties
    propertiesModal: {
      btnSave: 'Save Promotion Properties',
      titleEditing: 'Editing Promotion Properties',
      titleViewing: 'Viewing Promotion Properties',
      titleFor: 'for "{title}"',
      toastSaved: 'Promotion Properties Saved!',
      // vue-i18n plural form (`singular | plural`, read via $tc). The previous ICU `{count, plural, …}` syntax is
      // unsupported and made the production vue-i18n build throw a SyntaxError while rendering the modal.
      limitedAccessBanner: 'This promotion is assigned to {count} other property that you do not have access to. Showing your permitted properties only. | '
        + 'This promotion is assigned to {count} other properties that you do not have access to. Showing your permitted properties only.',
      searchPlaceholder: 'Search cities by name or property',
      assignedProperties: 'Assigned Properties:',
      unknownProvince: 'Unknown',
      selectAll: 'Select All',
      selectedCount: '({selected} / {total} selected)',
    },
  },
} as const;

export const promotionsFr = {
  promotions: {
    pageTitle: 'Promotions',
    addPromotion: 'Ajouter une promotion',
    missingCategory: 'Catégorie manquante!',
    limitedAccessWarning:
      'Les options de modification de cette promotion sont limitées en raison de votre niveau d\'accès',

    banners: {
      bulkEditTitle: 'Modifier plusieurs promotions',
      bulkEditMessage:
        'Pour modifier plusieurs promotions à la fois, accédez à la Mise à jour rapide pour effectuer vos changements plus rapidement et en moins de clics.',
      bulkEditAction: 'Accéder à la mise à jour rapide',
      categoryBoostMessage:
        'En catégorisant votre promotion, vous maximisez sa visibilité sur les plateformes d\'annonces locatives.',
    },

    fields: {
      title: 'Titre',
      category: 'Catégorie',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      numberOfProperties: 'Nombre de propriétés',
      lastModified: 'Dernière modification',
      status: 'Statut',
    },

    statuses: {
      placeholder: 'Sélectionner un statut',
      enabled: 'Actif',
      disabled: 'Désactivé',
      expired: 'Expiré',
      scheduled: 'Planifié',
    },

    categoryFields: {
      numOfMonths: 'Nombre de mois',
      leaseLength: 'Durée du bail',
      bonusMonths: 'Mois bonus',
      percentageDiscount: 'Remise en pourcentage (%)',
      valueAmount: 'Montant de la valeur',
      flatDiscount: 'Remise fixe',
      discountAmount: 'Montant de la remise',
    },

    // Per-category translations (keyed by BE category ID), handled in axios middleware
    categories: {
      1: {
        title: 'Loyer gratuit',
        description: 'Toute promotion offrant un loyer gratuit par mois.',
        example: '3 mois de loyer gratuit sur un bail de 12 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {},
      },
      2: {
        title: 'Remise sur le loyer (%)',
        description: 'Toute promotion offrant une remise en pourcentage sur le loyer par mois.',
        example: '20% de réduction sur le loyer pendant 3 mois sur un bail de 13 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          percentage_discount: { name: 'Remise en pourcentage (%) (recommandée)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Le pourcentage ne peut pas dépasser 99 %.' },
          1: { errorMessage: 'Une remise minimale de 5 % est requise pour apparaître sur les sites d\'annonces.' },
        },
      },
      3: {
        title: 'Remise sur le loyer (Montant)',
        description: 'Toute promotion offrant une remise fixe sur le loyer par mois.',
        example: '500$ de réduction pendant 2 mois sur un bail de 12 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          discount_amount: { name: 'Montant de la remise (recommandé)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Une remise minimale de 50 $ est requise pour apparaître sur les sites d\'annonces.' },
        },
      },
      4: {
        title: 'Stationnement gratuit',
        description: 'Toute promotion offrant un stationnement gratuit.',
        example: '3 mois de stationnement gratuit sur un bail de 12 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Assurez-vous que la durée du bail et la durée de la promotion ne sont pas identiques, sinon cela pourrait être classé comme un avantage plutôt qu\'une promotion.' },
        },
      },
      5: {
        title: 'Entreposage gratuit',
        description: 'Toute promotion offrant un entreposage gratuit.',
        example: '12 mois d\'entreposage gratuit sur un bail de 12 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Assurez-vous que la durée du bail et la durée de la promotion ne sont pas identiques, sinon cela pourrait être classé comme un avantage plutôt qu\'une promotion.' },
        },
      },
      6: {
        title: 'Cadeau',
        description: 'Toute promotion offrant un cadeau unique.',
        example: 'Carte-cadeau de 50$ à la signature d\'un bail.',
        fields: {
          value_amount: { name: 'Valeur du cadeau (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Une valeur minimale de 50 $ est requise pour apparaître sur les sites d\'annonces.' },
        },
      },
      7: {
        title: 'Remise sur service (%)',
        description: 'Toute promotion offrant une remise en pourcentage sur des services supplémentaires.',
        example: '20% de réduction sur la buanderie pendant 12 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          percentage_discount: { name: 'Remise en pourcentage (%) (recommandée)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Le pourcentage ne peut pas dépasser 99 %.' },
          1: { errorMessage: 'Une remise minimale de 5 % est requise pour apparaître sur les sites d\'annonces.' },
        },
      },
      8: {
        title: 'Remise sur service (Montant)',
        description: 'Toute promotion offrant une remise fixe sur des services supplémentaires.',
        example: '50$ de réduction sur le câble pendant 1 mois.',
        fields: {
          num_of_months: { name: 'Nombre de mois (recommandé)' },
          discount_amount: { name: 'Montant de la remise (recommandé)' },
          lease_length: { name: 'Durée du bail (recommandée)' },
        },
        rules: {
          0: { errorMessage: 'Une remise minimale de 50 $ est requise pour apparaître sur les sites d\'annonces.' },
        },
      },
      9: {
        title: 'Autre promotion',
        description: 'Toute promotion ne correspondant pas aux autres catégories.',
        example: '',
        fields: {},
        rules: {},
      },
    },

    single: {
      sectionDetails: 'Détails',
      sectionCategory: 'Catégorie',
      sectionLocations: 'Emplacements [{count}]',

      accessWarning: 'Les options de modification de cette promotion peuvent être limitées en raison de votre niveau d\'accès. Contactez votre administrateur pour obtenir l\'accès.',
      expiredWarning: 'Cette promotion a expiré et ne s\'affichera plus sur vos annonces ou votre site web',

      labelTitle: 'Titre de la promotion',
      labelImage: 'Télécharger une image',
      labelImagePrompt: 'Choisir une image',
      labelPdf: 'Télécharger un PDF',
      labelPdfPrompt: 'Choisir un PDF',
      labelStatus: 'Statut',
      labelStartDate: 'Date de début',
      labelEndDate: 'Date de fin',
      labelContent: 'Contenu de la promotion',
      labelCategory: 'Catégorie de la promotion',

      uploadFailed: 'Échec du téléchargement. Veuillez réessayer ou choisir un autre fichier.',
      fieldRequired: '{field} est obligatoire',
      mustBePositiveNumber: 'Doit être un nombre positif',
      startDateRequired: 'La date de début est obligatoire.',
      legalDisclaimer: 'Assurez-vous d\'inclure toutes les conditions générales légales applicables dans le contenu de votre promotion pour rester pleinement conforme et protégé.',
      exampleLabel: 'Exemple: {example}',

      btnEdit: 'Modifier',
      btnDuplicate: 'Dupliquer la promotion',
      btnSaveDetails: 'Enregistrer les détails',

      deleteTooltipNoPermission: 'Cette promotion ne peut pas être supprimée avec votre niveau d\'accès actuel.',
      duplicateTooltipSaveFirst: 'Enregistrez cette promotion avant de la dupliquer.',
      duplicateTooltipNoAccess: 'Vous ne pouvez pas dupliquer des promotions avec votre niveau d\'accès actuel.',
      duplicateTooltipDefault: 'Duplique la promotion actuelle. Notez que les modifications non enregistrées de la promotion actuelle ne seront pas dupliquées.',

      tooltipImage: 'Toute image que vous ajoutez à cette promotion apparaîtra sur les sites d\'annonces locatives applicables et/ou les sites web.',
      tooltipPdf: 'Les téléchargements PDF ne s\'affichent que sur les sites Rentsync. Si vous n\'en avez pas, le téléchargement d\'un PDF n\'aura aucun effet.',
      tooltipCategory: 'Les catégories de promotion s\'affichent uniquement sur les sites d\'annonces de location applicables qui prennent en charge le filtre et la balise de catégorie de promotion ; veuillez contacter le support pour plus de détails.',

      saveTooltipNoPermission: 'Vous n\'avez pas l\'autorisation de modifier cette promotion.',
      saveTooltipNeedTitleAndCategory: 'Veuillez saisir un titre de promotion et sélectionner une catégorie.',
      saveTooltipNeedTitle: 'Veuillez saisir un titre de promotion.',
      saveTooltipNeedCategory: 'Veuillez sélectionner une catégorie de promotion.',
      saveTooltipCategoryErrors: 'Veuillez corriger toutes les erreurs de validation de catégorie avant d\'enregistrer.',
      saveTooltipInvalidFile: 'Type de fichier non valide. Veuillez téléverser un format de fichier supporté.',
      saveTooltipNeedStartDate: 'Veuillez sélectionner une date de début.',
      saveTooltipEndBeforeStart: 'La date de fin doit être postérieure à la date de début.',

      removeTitle: 'Supprimer la promotion',
      removeText: 'Êtes-vous sûr de vouloir supprimer cette promotion ?',
      removeConfirm: 'Oui, supprimer la promotion',
      duplicateCompleteTitle: 'Duplication de la promotion terminée',
      duplicateCompleteText: 'Souhaitez-vous accéder à votre nouvelle promotion ?',
      duplicateCompleteConfirm: 'Oui, accéder à la nouvelle promotion',
      duplicateCompleteDeny: 'Non, rester ici',
      toastSaved: 'Les modifications de la promotion ont été enregistrées.',
      toastSaveError: 'Une erreur est survenue lors de l\'enregistrement de cette promotion ! Veuillez réessayer.',

      // Duplicate modal
      duplicateModalTitle: 'Dupliquer la promotion',
      duplicateModalBtnNext: 'Suivant',
      duplicateModalBtnSubmit: 'Dupliquer la promotion',
      duplicateModalIntro: 'Vous êtes sur le point de dupliquer « {title} ». Choisissez comment gérer les propriétés. Vous pourrez en ajouter de nouvelles à l\'écran suivant.',
      duplicateModalMoveLabel: 'Copier le contenu et déplacer les propriétés —',
      duplicateModalMoveDesc: 'Transfère les propriétés auxquelles vous avez accès de la promotion originale vers la nouvelle, en les retirant de l\'originale.',
      duplicateModalCopyLabel: 'Copier le contenu et copier les propriétés —',
      duplicateModalCopyDesc: 'Copier les propriétés auxquelles vous avez accès vers la nouvelle promotion en les conservant sur la promotion originale.',
      duplicateModalCopyWarning: '(Attention : cela peut créer des promotions en double.)',
      duplicateModalNoneLabel: 'Copier le contenu et sélectionner de nouvelles propriétés',
      duplicateModalSuffix: '[Dupliqué]',
      duplicateModalToastSuccess: 'Promotion dupliquée.',
      duplicateModalToastError: 'Une erreur est survenue lors de la duplication de cette promotion.',
    },

    // RsModalPromotionProperties
    propertiesModal: {
      btnSave: 'Enregistrer les propriétés de la promotion',
      titleEditing: 'Modification des propriétés de la promotion',
      titleViewing: 'Affichage des propriétés de la promotion',
      titleFor: 'pour « {title} »',
      toastSaved: 'Propriétés de la promotion enregistrées!',
      limitedAccessBanner: 'Cette promotion est assignée à {count} autre(s) propriété(s) auxquelles vous n\'avez pas accès. Affichage uniquement de vos propriétés autorisées.',
      searchPlaceholder: 'Rechercher des villes par nom ou propriété',
      assignedProperties: 'Propriétés assignées :',
      unknownProvince: 'Inconnu',
      selectAll: 'Tout sélectionner',
      selectedCount: '({selected} / {total} sélectionné) | ({selected} / {total} sélectionnés)',
    },
  },
} as const;
